import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { flatten, uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { DatapointFieldListActions } from './datapoint-field-list.actions';
import { ColumnType } from '../../features/market-comparison/models/column-type.enum';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointFieldService } from '../../features/unified-datapoint/services/datapoint-field.service';
import { AppActions } from '../app/app.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class DatapointFieldListEffects {
  private service = inject(DatapointFieldService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);

  loadTopicsSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const datapointFieldIds = uniq(
          flatten(
            store.topicList?.topics?.map((t) =>
              t.columns.filter((c) => c.type === ColumnType.Field).map((c) => c.value as string)
            )
          )
        );
        return DatapointFieldListActions.LoadDatapointFields({ datapointFieldIds });
      })
    )
  );

  refreshDatapointFieldEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.RefreshDatapointField),
      switchMap((action) =>
        this.service
          .getById(action.id)
          .pipe(map((field) => DatapointFieldListActions.RefreshDatapointFieldSuccess({ item: field })))
      )
    )
  );

  loadDatapointFieldsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.LoadDatapointFields),
      withLatestFrom(this.store),
      mergeMap(([action, store]) => {
        let source = this.service.getAll();

        if (action.datapointFieldIds) {
          const existingIds = new Set(store.datapointFieldList.fields?.map((f) => f.id) || []);
          const missingIds = action.datapointFieldIds.filter((id) => !existingIds.has(id));

          if (missingIds.length === 0) {
            source = this.service.getByIds(missingIds);
          } else {
            return of(DatapointFieldListActions.LoadDatapointFieldsSuccess({ list: [] }));
          }
        }

        if (action.withTemplate) {
          source = this.service.getAllWithTemplates();
        }

        return source.pipe(
          map((list) => DatapointFieldListActions.LoadDatapointFieldsSuccess({ list })),
          reportEffectError(DatapointFieldListActions.LoadDatapointFieldsFail)
        );
      })
    )
  );

  setDatapointFieldActivityEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.SetDatapointFieldActivity),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.setDatapointFieldActivity(action.id, action.active).pipe(
          map((field) =>
            DatapointFieldListActions.SetDatapointFieldActivitySuccess({
              id: field?.id,
              active: field?.active,
            })
          ),
          catchError((error) =>
            of(DatapointFieldListActions.SetDatapointFieldActivityFail({ error: error.toString() }))
          )
        )
      )
    )
  );

  setDatapointFieldActivityFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.SetDatapointFieldActivityFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update the field',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  deleteDatapointFieldEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.DeleteDatapointField),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.delete(action.id).pipe(
          map((field) =>
            DatapointFieldListActions.DeleteDatapointFieldSuccess({
              id: action.id,
              navigate: action.navigate,
            })
          ),
          catchError((error) => of(DatapointFieldListActions.DeleteDatapointFieldFail({ error: error.toString() })))
        )
      )
    )
  );

  deleteDatapointFieldSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.DeleteDatapointFieldSuccess),
      map(() =>
        AppActions.PushNotification({
          message: 'Datapoinnt field has been deleted',
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  deleteDatapointFielFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.DeleteDatapointFieldFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to delete the field',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  deleteFieldEffectNavigation$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatapointFieldListActions.DeleteDatapointFieldSuccess),
        filter((action) => !!action.navigate),
        map((action) => this.router.navigate(['/', SemerisRoutes.datapoint.root]))
      ),
    { dispatch: false }
  );

  cloneModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldListActions.CloneModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.clone(action.id).pipe(
          map((datapointField) => DatapointFieldListActions.CloneModelSuccess({ datapointField })),
          reportEffectError(DatapointFieldListActions.CloneModelFail)
        )
      )
    )
  );

  cloneChecklistSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatapointFieldListActions.CloneModelSuccess),
        map((action) =>
          this.router.navigate(['/', SemerisRoutes.datapoint.root, 'field', 'user', action.datapointField.id])
        )
      ),
    { dispatch: false }
  );
}
